// The section emits its own <script defer> tag, so two hero sliders on one page
// load this file twice. customElements.define throws on a second definition.
if (!customElements.get('hero-slider')) {
  customElements.define(
    'hero-slider',
    class HeroSlider extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('.hero-slider__slide'));
        this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
        this.live = this.querySelector('[data-hs-live]');

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.duration = this.reducedMotion ? 0 : Number(this.dataset.duration) || 0;
        this.interval = Number(this.dataset.interval) || 0;
        this.autoplay = this.hasAttribute('data-autoplay') && this.interval > 0 && !this.reducedMotion && this.slides.length > 1;
        this.classList.toggle('no-autoplay', !this.autoplay);

        this.userPaused = false;
        this.locked = false;
        this.swiped = false;
        this.drag = null;
        // Reasons autoplay is temporarily held: hover, focus, hidden, offscreen, editor.
        this.holds = new Set();
        if (document.hidden) this.holds.add('hidden');

        this.abort = new AbortController();
        const options = { signal: this.abort.signal };

        if (this.slides.length > 1) this.bindNavigation(options);

        document.addEventListener('visibilitychange', () => this.setHold('hidden', document.hidden), options);
        this.observer = new IntersectionObserver(([entry]) => this.setHold('offscreen', !entry.isIntersecting));
        this.observer.observe(this);

        this.updateVideos();
        this.updatePlayback();
      }

      disconnectedCallback() {
        this.abort.abort();
        this.observer.disconnect();
        clearTimeout(this.timer);
        clearTimeout(this.lockTimer);
        clearTimeout(this.swipeTimer);
      }

      bindNavigation(options) {
        this.addEventListener(
          'click',
          (event) => {
            const target = event.target.closest('[data-hs-prev], [data-hs-next], [data-hs-page], [data-hs-pause]');
            if (!target) return;
            if (target.hasAttribute('data-hs-prev')) this.goTo(this.index - 1, { manual: true });
            else if (target.hasAttribute('data-hs-next')) this.goTo(this.index + 1, { manual: true });
            else if (target.hasAttribute('data-hs-page')) this.goTo(Number(target.dataset.hsPage), { manual: true });
            else this.togglePause(target);
          },
          options
        );

        // A drag that changed slide must not also activate the link under the pointer.
        this.addEventListener(
          'click',
          (event) => {
            if (!this.swiped) return;
            event.preventDefault();
            event.stopPropagation();
            this.swiped = false;
          },
          { ...options, capture: true }
        );

        this.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'ArrowRight') this.goTo(this.index + 1, { manual: true });
            else if (event.key === 'ArrowLeft') this.goTo(this.index - 1, { manual: true });
          },
          options
        );

        // No setPointerCapture: capturing retargets the click and breaks links inside slides.
        const track = this.querySelector('.hero-slider__track');
        track.addEventListener(
          'pointerdown',
          (event) => {
            // Swipe is for touch and pen; desktop has arrows and arrow keys.
            if (event.pointerType === 'mouse') return;
            this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY };
          },
          options
        );
        track.addEventListener('dragstart', (event) => event.preventDefault(), options);
        window.addEventListener('pointerup', (event) => this.endDrag(event), options);
        window.addEventListener(
          'pointercancel',
          (event) => {
            if (this.drag && event.pointerId === this.drag.id) this.drag = null;
          },
          options
        );

        this.addEventListener('mouseenter', () => this.setHold('hover', true), options);
        this.addEventListener('mouseleave', () => this.setHold('hover', false), options);
        // Only keyboard focus holds autoplay; a mouse click on the pause button must not.
        this.addEventListener('focusin', (event) => this.setHold('focus', event.target.matches(':focus-visible')), options);
        this.addEventListener(
          'focusout',
          (event) => {
            if (!this.contains(event.relatedTarget)) this.setHold('focus', false);
          },
          options
        );

        this.addEventListener(
          'shopify:block:select',
          (event) => {
            const index = this.slides.indexOf(event.target.closest('.hero-slider__slide'));
            if (index < 0) return;
            this.setHold('editor', true);
            this.goTo(index, { force: true });
          },
          options
        );
        this.addEventListener('shopify:block:deselect', () => this.setHold('editor', false), options);
      }

      endDrag(event) {
        if (!this.drag) return;
        if (event.pointerId !== this.drag.id) return;
        const dx = event.clientX - this.drag.x;
        const dy = event.clientY - this.drag.y;
        this.drag = null;
        if (Math.abs(dx) <= 40 || Math.abs(dx) <= Math.abs(dy)) return;

        this.swiped = true;
        clearTimeout(this.swipeTimer);
        // Touch swipes often produce no click at all, so the flag must not linger.
        this.swipeTimer = setTimeout(() => (this.swiped = false), 50);
        this.goTo(this.index + (dx < 0 ? 1 : -1), { manual: true });
      }

      goTo(target, { manual = false, force = false } = {}) {
        const count = this.slides.length;
        const next = ((target % count) + count) % count;
        if (count < 2 || next === this.index || (this.locked && !force)) return;

        const from = this.slides[this.index];
        const to = this.slides[next];

        // Set the direction and flush styles first, so the incoming slide starts
        // from the correct side before is-active gives it a transition.
        this.slides.forEach((slide) => slide.classList.remove('is-leaving'));
        this.dataset.direction = target > this.index ? 'next' : 'prev';
        void this.offsetWidth;

        from.classList.remove('is-active');
        from.classList.add('is-leaving');
        from.setAttribute('aria-hidden', 'true');
        to.classList.add('is-active');
        to.setAttribute('aria-hidden', 'false');
        this.index = next;

        this.locked = true;
        clearTimeout(this.lockTimer);
        this.lockTimer = setTimeout(() => {
          from.classList.remove('is-leaving');
          this.locked = false;
        }, this.duration);

        this.querySelectorAll('[data-hs-page]').forEach((page) => {
          if (Number(page.dataset.hsPage) === next) page.setAttribute('aria-current', 'true');
          else page.removeAttribute('aria-current');
        });
        const current = this.querySelector('[data-hs-current]');
        if (current) current.textContent = next + 1;

        if (manual && this.live) {
          this.live.textContent = `${to.getAttribute('aria-roledescription')} ${to.getAttribute('aria-label')}`;
        }

        this.updateVideos();
        this.updatePlayback();
      }

      togglePause(button) {
        this.userPaused = !this.userPaused;
        this.classList.toggle('is-paused', this.userPaused);
        button.setAttribute('aria-label', this.userPaused ? button.dataset.labelPlay : button.dataset.labelPause);
        this.updateVideos();
        this.updatePlayback();
      }

      setHold(reason, on) {
        if (on) this.holds.add(reason);
        else this.holds.delete(reason);
        if (reason === 'hidden' || reason === 'offscreen') this.updateVideos();
        this.updatePlayback();
      }

      // Restarts the timer from zero on every call; the progress bar animation is
      // keyed off is-playing and the active page, so it restarts in step.
      updatePlayback() {
        const playing = this.autoplay && !this.userPaused && this.holds.size === 0;
        this.classList.toggle('is-playing', playing);
        clearTimeout(this.timer);
        if (playing) this.timer = setTimeout(() => this.goTo(this.index + 1), this.interval);
      }

      updateVideos() {
        const allowed = !this.reducedMotion && !this.userPaused && !this.holds.has('hidden') && !this.holds.has('offscreen');
        this.slides.forEach((slide, index) => {
          const video = slide.querySelector('video');
          if (!video) return;
          // getClientRects is empty when the video is display:none (mobile still in use).
          if (allowed && index === this.index && video.getClientRects().length) {
            video.muted = true;
            Promise.resolve(video.play()).catch(() => {});
          } else {
            video.pause();
          }
        });
      }
    }
  );
}